/**
 * Admin Organizations Page
 *
 * Lists all organizations with search, pagination, and CRUD actions.
 */

import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  DataTable,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ImageUpload,
  Inline,
  Input,
  Label,
  OrgLogo,
  PageHeader,
  Stack,
  Text,
} from '@cortex/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { useApi, useFetchWithAuth } from '@/hooks/use-api';
import {
  DEFAULT_PAGINATION,
  formatDate,
  type PaginationInfo,
} from '@/lib/admin-utils';

export const Route = createFileRoute('/_app/admin/organizations/')({
  component: AdminOrganizationsPage,
});

interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  created_at: string;
  updated_at: string;
}

function AdminOrganizationsPage() {
  const navigate = useNavigate();
  const getApi = useApi();
  const fetchWithAuth = useFetchWithAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [pagination, setPagination] =
    useState<PaginationInfo>(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');

  // Create modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgSlug, setNewOrgSlug] = useState('');
  const [newOrgEmail, setNewOrgEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [editOrgName, setEditOrgName] = useState('');
  const [editOrgSlug, setEditOrgSlug] = useState('');
  const [editOrgLogoUrl, setEditOrgLogoUrl] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingOrg, setDeletingOrg] = useState<Organization | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch organizations
  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const api = await getApi();
      const query: Record<string, string> = {
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      };
      if (searchDebounce) {
        query.search = searchDebounce;
      }

      const response = await api.admin.organizations.$get({ query });
      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }

      const json = await response.json();
      if ('data' in json) {
        const data = json.data as unknown as {
          items: Organization[];
          pagination: PaginationInfo;
        };
        setOrganizations(data.items);
        setPagination(data.pagination);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load organizations'
      );
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchDebounce, getApi]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Handle name change and auto-generate slug
  const handleNameChange = (name: string) => {
    setNewOrgName(name);
    if (!newOrgSlug || newOrgSlug === generateSlug(newOrgName)) {
      setNewOrgSlug(generateSlug(name));
    }
  };

  // Create organization
  const handleCreate = async () => {
    setCreating(true);
    setCreateError(null);

    try {
      const api = await getApi();
      const response = await api.admin.organizations.$post({
        json: {
          name: newOrgName,
          slug: newOrgSlug,
          billingEmail: newOrgEmail,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          ('error' in data && typeof data.error === 'string'
            ? data.error
            : null) || 'Failed to create organization'
        );
      }

      // Reset form and close modal
      setNewOrgName('');
      setNewOrgSlug('');
      setNewOrgEmail('');
      setCreateModalOpen(false);

      // Refresh list
      fetchOrganizations();
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : 'Failed to create organization'
      );
    } finally {
      setCreating(false);
    }
  };

  // Open edit modal
  const openEditModal = (org: Organization) => {
    setEditingOrg(org);
    setEditOrgName(org.name);
    setEditOrgSlug(org.slug);
    setEditOrgLogoUrl(org.logo_url);
    setUpdateError(null);
    setEditModalOpen(true);
  };

  // Upload logo
  const handleLogoUpload = async (file: File) => {
    if (!editingOrg) return;

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await fetchWithAuth(
        `/admin/organizations/${editingOrg.id}/logo`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload logo');
      }

      const { data } = await response.json();
      setEditOrgLogoUrl(data.logo_url);
      setEditingOrg({ ...editingOrg, logo_url: data.logo_url });
    } catch (err) {
      setUpdateError(
        err instanceof Error ? err.message : 'Failed to upload logo'
      );
    } finally {
      setUploadingLogo(false);
    }
  };

  // Delete logo
  const handleLogoDelete = async () => {
    if (!editingOrg) return;

    setDeletingLogo(true);
    try {
      const response = await fetchWithAuth(
        `/admin/organizations/${editingOrg.id}/logo`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete logo');
      }

      setEditOrgLogoUrl(null);
      setEditingOrg({ ...editingOrg, logo_url: null });
    } catch (err) {
      setUpdateError(
        err instanceof Error ? err.message : 'Failed to delete logo'
      );
    } finally {
      setDeletingLogo(false);
    }
  };

  // Update organization
  const handleUpdate = async () => {
    if (!editingOrg) return;

    setUpdating(true);
    setUpdateError(null);

    try {
      const response = await fetchWithAuth(
        `/admin/organizations/${editingOrg.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: editOrgName,
            slug: editOrgSlug,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update organization');
      }

      setEditModalOpen(false);
      setEditingOrg(null);
      fetchOrganizations();
    } catch (err) {
      setUpdateError(
        err instanceof Error ? err.message : 'Failed to update organization'
      );
    } finally {
      setUpdating(false);
    }
  };

  // Open delete modal
  const openDeleteModal = (org: Organization) => {
    setDeletingOrg(org);
    setDeleteError(null);
    setDeleteModalOpen(true);
  };

  // Delete organization
  const handleDelete = async () => {
    if (!deletingOrg) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      const response = await fetchWithAuth(
        `/admin/organizations/${deletingOrg.id}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok && response.status !== 204) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete organization');
      }

      setDeleteModalOpen(false);
      setDeletingOrg(null);
      fetchOrganizations();
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to delete organization'
      );
    } finally {
      setDeleting(false);
    }
  };

  // Table columns
  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (org: Organization) => (
        <Inline gap="sm">
          <OrgLogo src={org.logo_url} name={org.name} size="sm" />
          <Stack gap="none">
            <Text weight="medium">{org.name}</Text>
            <Text color="muted" size="sm">
              {org.slug}
            </Text>
          </Stack>
        </Inline>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (org: Organization) => (
        <Text size="sm" color="muted">
          {formatDate(org.created_at)}
        </Text>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (org: Organization) => (
        <Inline gap="xs" justify="end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditModal(org)}
            className="h-8 w-8 p-0"
          >
            <Pencil className="h-4 w-4" />
            <Text as="span" className="sr-only">
              Edit
            </Text>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openDeleteModal(org)}
            className="text-destructive hover:text-destructive h-8 w-8 p-0"
          >
            <Trash2 className="h-4 w-4" />
            <Text as="span" className="sr-only">
              Delete
            </Text>
          </Button>
        </Inline>
      ),
    },
  ];

  return (
    <Stack gap="lg" className="p-6">
      {/* Header */}
      <PageHeader
        title="Organizations"
        subtitle="Manage organizations and their settings"
        actions={
          <Button onClick={() => setCreateModalOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            New Organization
          </Button>
        }
      />

      {/* Search */}
      <Inline gap="md">
        <Stack className="flex-1">
          <Input
            type="text"
            placeholder="Search organizations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftAddon={<Search className="h-4 w-4" />}
          />
        </Stack>
      </Inline>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {error}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchOrganizations}
              className="mt-2"
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Table */}
      <DataTable
        columns={columns}
        data={organizations}
        loading={loading}
        emptyMessage="No organizations found"
        pagination={{
          page: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
        }}
        onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
        onRowClick={(org) =>
          navigate({
            to: '/admin/organizations/$slug',
            params: { slug: org.slug },
          })
        }
      />

      {/* Create Modal */}
      <Dialog
        open={createModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setCreateModalOpen(false);
            setNewOrgName('');
            setNewOrgSlug('');
            setNewOrgEmail('');
            setCreateError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogDescription>
              Add a new organization to the system.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Stack gap="md">
              {createError && (
                <Alert variant="destructive">
                  <AlertDescription>{createError}</AlertDescription>
                </Alert>
              )}
              <Stack gap="xs">
                <Label htmlFor="orgName">Organization Name</Label>
                <Input
                  id="orgName"
                  type="text"
                  placeholder="Acme Corporation"
                  value={newOrgName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="mt-1"
                />
              </Stack>
              <Stack gap="xs">
                <Label htmlFor="orgSlug">Slug</Label>
                <Input
                  id="orgSlug"
                  type="text"
                  placeholder="acme-corporation"
                  value={newOrgSlug}
                  onChange={(e) => setNewOrgSlug(e.target.value)}
                  className="mt-1"
                />
                <Text color="muted" size="xs" className="mt-1">
                  URL-friendly identifier. Only lowercase letters, numbers, and
                  hyphens.
                </Text>
              </Stack>
              <Stack gap="xs">
                <Label htmlFor="orgEmail">Billing Email</Label>
                <Input
                  id="orgEmail"
                  type="email"
                  placeholder="billing@example.com"
                  value={newOrgEmail}
                  onChange={(e) => setNewOrgEmail(e.target.value)}
                  className="mt-1"
                />
                <Text color="muted" size="xs" className="mt-1">
                  Email address for billing and invoices.
                </Text>
              </Stack>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateModalOpen(false);
                setNewOrgName('');
                setNewOrgSlug('');
                setNewOrgEmail('');
                setCreateError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!newOrgName || !newOrgSlug || !newOrgEmail}
              loading={creating}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditModalOpen(false);
            setEditingOrg(null);
            setUpdateError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Organization</DialogTitle>
            <DialogDescription>Update organization details.</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Stack gap="md">
              {updateError && (
                <Alert variant="destructive">
                  <AlertDescription>{updateError}</AlertDescription>
                </Alert>
              )}
              {/* Logo Upload */}
              <Stack gap="xs">
                <Label>Logo</Label>
                <Inline gap="md" align="start">
                  <ImageUpload
                    src={editOrgLogoUrl}
                    placeholder={
                      <OrgLogo name={editOrgName || 'Organization'} size="lg" />
                    }
                    alt={`${editOrgName} logo`}
                    onUpload={handleLogoUpload}
                    onDelete={editOrgLogoUrl ? handleLogoDelete : undefined}
                    uploading={uploadingLogo}
                    deleting={deletingLogo}
                    size="lg"
                  />
                  <Stack className="flex-1">
                    <Text color="muted" size="sm">
                      Upload a square logo (256x256 recommended).
                    </Text>
                    <Text color="muted" size="xs" className="mt-1">
                      PNG, JPEG, or WebP. Max 2MB.
                    </Text>
                  </Stack>
                </Inline>
              </Stack>
              <Stack gap="xs">
                <Label htmlFor="editOrgName">Organization Name</Label>
                <Input
                  id="editOrgName"
                  type="text"
                  placeholder="Acme Corporation"
                  value={editOrgName}
                  onChange={(e) => setEditOrgName(e.target.value)}
                  className="mt-1"
                />
              </Stack>
              <Stack gap="xs">
                <Label htmlFor="editOrgSlug">Slug</Label>
                <Input
                  id="editOrgSlug"
                  type="text"
                  placeholder="acme-corporation"
                  value={editOrgSlug}
                  onChange={(e) => setEditOrgSlug(e.target.value)}
                  className="mt-1"
                />
                <Text color="muted" size="xs" className="mt-1">
                  URL-friendly identifier. Only lowercase letters, numbers, and
                  hyphens.
                </Text>
              </Stack>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditModalOpen(false);
                setEditingOrg(null);
                setUpdateError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={!editOrgName || !editOrgSlug}
              loading={updating}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteModalOpen(false);
            setDeletingOrg(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Organization</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this organization? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Stack gap="md">
              {deleteError && (
                <Alert variant="destructive">
                  <AlertDescription>{deleteError}</AlertDescription>
                </Alert>
              )}
              {deletingOrg && (
                <Card className="bg-surface-2">
                  <CardContent className="p-4">
                    <Inline gap="sm">
                      <OrgLogo
                        src={deletingOrg.logo_url}
                        name={deletingOrg.name}
                        size="md"
                      />
                      <Stack gap="none">
                        <Text weight="medium">{deletingOrg.name}</Text>
                        <Text color="muted" size="sm">
                          {deletingOrg.slug}
                        </Text>
                      </Stack>
                    </Inline>
                  </CardContent>
                </Card>
              )}
              <Text color="muted" size="sm">
                All users and projects associated with this organization will
                also be affected.
              </Text>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setDeletingOrg(null);
                setDeleteError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              loading={deleting}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
